import { ProductDataShape } from '../ProductDataShape'
import { SkinProductOption } from '../types/productTypes'
import { logDebug } from '../debug/debugLogging'

/**
 * Tries to find a product matching the given item number.
 * In order of precedence, looks up:
 *  1. Skin with an item number that matches
 *  2. Skin included with a composite that matches
 *  2. Skin compatible with a composite that matches (but includes no skin)
 * @param initialProductItemNo
 * @param products
 */

export interface DeepLinkData {
  skinProductOption: SkinProductOption
  includedMechsItemNo: string | null
}

export function resolveDeeplinkProduct(initialProductItemNo: string | null, products: ProductDataShape): DeepLinkData | null {
  if (initialProductItemNo === null) {
    return null
  }

  const itemNo = initialProductItemNo.toLowerCase()

  const skinMatch = products.skinGridProducts
    .find(item => item.itemNo.toLowerCase() === itemNo)

  if (skinMatch) {
    logDebug('resolveDeeplinkProduct', `found skin match for ${itemNo}:`, skinMatch)
    return { skinProductOption: skinMatch, includedMechsItemNo: null }
  }

  const compositeMatch = products.grids
    .find(item => item.itemNo.toLowerCase() === itemNo)

  if (compositeMatch) {
    const compositeIncludedSkinItemNo = compositeMatch.includedSkinItemNo

    if (compositeIncludedSkinItemNo) {
      const includedSkin = products.skins
        .find(item => item.itemNo === compositeIncludedSkinItemNo)

      if (!includedSkin) {
        logDebug('resolveDeeplinkProduct', `no skin found for ${compositeIncludedSkinItemNo}`)
        return null
      }

      // find a skin/grid option with the specified skin and exact orientation
      let skinIncludedWithComposite: SkinProductOption | undefined

      if (compositeMatch.orientation === 'Both') {
        skinIncludedWithComposite = products.skinGridProducts
          .find(skinGridProduct => skinGridProduct.itemNo === compositeIncludedSkinItemNo)// && isSkinOrientationSupported(includedSkin, compositeMatch.orientation))
      } else {
        // find a skin/grid option with the specified skin and exact orientation
        skinIncludedWithComposite = products.skinGridProducts
          .find(skinGridProduct => skinGridProduct.itemNo === compositeIncludedSkinItemNo && compositeMatch.orientation === skinGridProduct.orientation)
      }

      if (!skinIncludedWithComposite) {
        logDebug(`no skin found for composite ${compositeMatch.itemNo}`)
        return null
      }

      logDebug('resolveDeeplinkProduct', `found skin match ${skinIncludedWithComposite.itemNo} included with composite ${itemNo}:`, compositeMatch)

      // Return mech included with composite so we can pre-populate it in the ButtonConfigurationScreen.
      return { skinProductOption: skinIncludedWithComposite, includedMechsItemNo: compositeMatch.includedMechItemNo }
    }

    const skinWithComposite = products.skinGridProducts
      .find(item => item.grid === compositeMatch.itemNo)

    if (skinWithComposite) {
      logDebug('resolveDeeplinkProduct', `found skin match ${skinWithComposite.itemNo} for ${itemNo} in composite:`, compositeMatch)
      return { skinProductOption: skinWithComposite, includedMechsItemNo: null }
    }
  }

  return null
}
